import type { BlockPos } from './core/BlockPos.js'
import { BlockState } from './core/BlockState.js'
import { MultiStructure } from './MultiStructure.js'
import type { StructureRegion } from './MultiStructure.js'
import { NbtCompound } from './nbt/tags/NbtCompound.js'
import { NbtType } from './nbt/tags/NbtType.js'
import { Structure } from './Structure.js'

function getTriple(tag: NbtCompound): BlockPos {
	return [tag.getNumber('x'), tag.getNumber('y'), tag.getNumber('z')]
}

export function litematicToStructure(root: NbtCompound, limit: number = -1) {
	const enclosingSize = root.getCompound('Metadata').getCompound('EnclosingSize')
	const [width, height, length] = getTriple(enclosingSize)

	if (limit > 0 && (width > limit || height > limit || length > limit)) {
		throw new Error(`Structure size exceeds limit: ${width}x${height}x${length} (limit: ${limit})`)
	}

	const regions: StructureRegion[] = []
	root.getCompound('Regions').forEach((name, region) => {
		if (!region.isCompound()) return
		const pos = getTriple(region.getCompound('Position'))
		const size = getTriple(region.getCompound('Size'))
		for (let i = 0; i < 3; i += 1) {
			if (size[i] < 0) {
				pos[i] += size[i]
				size[i] = -size[i]
			}
		}
		const volume = size[0] * size[1] * size[2]
		const palette = region.getList('BlockStatePalette', NbtType.Compound).map(BlockState.fromNbt)
		const blockStates = region.getLongArray('BlockStates')
		const tempDataview = new DataView(new Uint8Array(8).buffer)
		const statesData = blockStates.map(long => {
			tempDataview.setInt32(0, Number(long.getAsPair()[0]))
			tempDataview.setInt32(4, Number(long.getAsPair()[1]))
			return tempDataview.getBigUint64(0)
		})

		const bits = Math.max(2, Math.ceil(Math.log2(palette.length)))
		const bigBits = BigInt(bits)
		const bitMask = BigInt(Math.pow(2, bits) - 1)
		let state = 0
		let data = statesData[state]
		let dataLength = BigInt(64)
		const arr: number[] = []
		for (let j = 0; j < volume; j += 1) {
			if (dataLength < bits) {
				state += 1
				const newData = statesData[state] ?? BigInt(0)
				data = (newData << dataLength) | data
				dataLength += BigInt(64)
			}
			let paletteId = Number(data & bitMask)
			if (paletteId > palette.length - 1) {
				paletteId = 0
			}
			arr.push(paletteId)
			data >>= bigBits
			dataLength -= bigBits
		}

		const blocks: { pos: BlockPos, state: number, nbt?: NbtCompound }[] = []
		const blockEntities = new Map<string, NbtCompound>()
		region.getList('TileEntities', NbtType.Compound).forEach((tag) => {
			const entityPos = getTriple(tag).toString()
			const copy = NbtCompound.fromJson(tag.toJson())
			copy.delete('x')
			copy.delete('y')
			copy.delete('z')
			blockEntities.set(entityPos, copy)
		})
		for (let x = 0; x < size[0]; x += 1) {
			for (let y = 0; y < size[1]; y += 1) {
				for (let z = 0; z < size[2]; z += 1) {
					const index = (y * size[0] * size[2]) + z * size[0] + x
					const blockPos: BlockPos = [x, y, z]
					blocks.push({
						pos: blockPos,
						state: arr[index],
						nbt: blockEntities.get(blockPos.toString()),
					})
				}
			}
		}
		regions.push({ pos, structure: new Structure(size, palette, blocks), name })
	})

	const minPos: BlockPos = [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER]
	for (const region of regions) {
		for (let i = 0; i < 3; i += 1) {
			minPos[i] = Math.min(minPos[i], region.pos[i])
		}
	}
	for (const region of regions) {
		for (let i = 0; i < 3; i += 1) {
			region.pos[i] -= minPos[i]
		}
	}

	return new MultiStructure([width, height, length], regions)
}
